
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from "lucide-react";
import { AIConsultation } from "@/components/AIConsultation";
import { analyzeHormonalAxis, getHormoneReference, getStatusColor } from "@/utils/hormonalAnalysis";
import { MetabolicAnalysis } from "@/components/MetabolicAnalysis";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

// Cores específicas para cada hormônio
const hormoneColors = {
  estradiol: "#FF6B6B",
  progesterona: "#4ECDC4",
  lh: "#45B7D1",
  fsh: "#96CEB4",
  amh: "#FFEEAD",
  testosterona: "#D4A5A5",
  shbg: "#9B786F",
  prolactina: "#A8E6CF",
  cortisol: "#FFD3B6",
  dht: "#FF8B94",
  glicemia: "#B5EAD7",
  insulina: "#C7CEEA",
  ldl: "#E2F0CB",
  hdl: "#FFDAC1",
  triglicerides: "#FFB7B2",
  hematocrito: "#E2F0CB"
};

export function AdvancedCalculator({ onBack, gender, onExamDataUpdate, onResultsUpdate }) {
  const { toast } = useToast();
  const [examValues, setExamValues] = useState({});
  const [clinicalData, setClinicalData] = useState({
    age: "",
    lastPeriod: "",
    hasSymptoms: false
  });
  const [showResults, setShowResults] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  const handleExamSubmit = (e) => {
    e.preventDefault();
    
    // Análise hormonal
    const results = analyzeHormonalAxis(examValues, gender);
    
    setAnalysisResults(results);
    setShowResults(true);
    
    // Atualizar dados no componente pai
    if (onExamDataUpdate) onExamDataUpdate(examValues);
    if (onResultsUpdate) onResultsUpdate(results);

    toast({
      title: "Análise Concluída",
      description: "Seus resultados estão prontos",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExamValues(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleClinicalDataChange = (e) => {
    const { name, value } = e.target;
    setClinicalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const reference = getHormoneReference(data.name.toLowerCase(), gender);
      const status = data.value < reference.min ? "Baixo" : 
                    data.value > reference.max ? "Elevado" : "Normal";
      const statusColor = status === "Normal" ? "#4ade80" :
                         status === "Elevado" ? "#f59e0b" : "#ef4444";
  
      return (
        <div className="bg-[#1a1a2e]/95 p-4 rounded-lg border border-[#ff3366]/20 shadow-lg backdrop-blur-sm">
          <h4 className="text-[#ff3366] font-bold text-lg mb-2">{label}</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/80">Valor:</span>
              <span className="text-white font-semibold">
                {data.value} {reference.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Referência:</span>
              <span className="text-white">
                {reference.min}-{reference.max} {reference.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Status:</span>
              <span className="px-2 py-1 rounded text-sm font-semibold" style={{ color: statusColor }}>
                {status}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className="mt-10 max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-[#ff3366] hover:text-[#ff3366]/80 hover:bg-[#1a1a2e]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h2 className="text-xl font-bold text-[#ff3366]">
          Calculadora Avançada - {gender}
        </h2>
      </div>

      <form onSubmit={handleExamSubmit} className="space-y-6">
        {/* Dados Clínicos */}
        <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Dados Clínicos
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="number"
              placeholder="Idade"
              name="age"
              value={clinicalData.age}
              onChange={handleClinicalDataChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              required
            />
            {gender === "Feminino" && (
              <div className="flex flex-col gap-2">
                <label htmlFor="lastPeriod" className="text-white">
                  Data da Última Menstruação
                </label>
                <input
                  id="lastPeriod"
                  type="date"
                  name="lastPeriod"
                  value={clinicalData.lastPeriod}
                  onChange={handleClinicalDataChange}
                  className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* Exames Hormonais */}
        <div className="grid gap-4 md:grid-cols-2">
          {gender === "Masculino" ? (
            <>
              <input
                type="number"
                name="testosterona"
                placeholder="Testosterona Total (ng/dL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="shbg"
                placeholder="SHBG (nmol/L)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="estradiol"
                placeholder="Estradiol (pg/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="dht"
                placeholder="DHT (pg/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="lh"
                placeholder="LH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="fsh"
                placeholder="FSH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="prolactina"
                placeholder="Prolactina (ng/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="cortisol"
                placeholder="Cortisol (μg/dL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
            </>
          ) : (
            <>
              <input
                type="number"
                name="estradiol"
                placeholder="Estradiol (pg/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="progesterona"
                placeholder="Progesterona (ng/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="amh"
                placeholder="AMH (ng/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="lh"
                placeholder="LH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="fsh"
                placeholder="FSH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="prolactina"
                placeholder="Prolactina (ng/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="shbg"
                placeholder="SHBG (nmol/L)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="cortisol"
                placeholder="Cortisol (μg/dL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
            </>
          )}
        </div>

        {/* Exames Metabólicos */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Exames Metabólicos
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="number"
              name="glicemia"
              placeholder="Glicemia (mg/dL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
            <input
              type="number"
              name="insulina"
              placeholder="Insulina (μU/mL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
            <input
              type="number"
              name="ldl"
              placeholder="LDL (mg/dL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
            <input
              type="number"
              name="hdl"
              placeholder="HDL (mg/dL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
            <input
              type="number"
              name="triglicerides"
              placeholder="Triglicerídeos (mg/dL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
            <input
              type="number"
              name="hematocrito"
              placeholder="Hematócrito (%)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#ff3366] hover:bg-[#ff3366]/80 text-white"
        >
          Analisar Resultados
        </Button>
      </form>

      {showResults && analysisResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 space-y-6"
        >
          {/* Gráfico de Hormônios */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Perfil Hormonal
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.entries(examValues).map(([hormone, value]) => {
                  const reference = getHormoneReference(hormone, gender);
                  return {
                    name: hormone.toUpperCase(),
                    value: value,
                    min: reference.min,
                    max: reference.max,
                    unit: reference.unit,
                    color: hormoneColors[hormone.toLowerCase()] || "#ff3366"
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ff3366" opacity={0.1} />
                  <XAxis
                    dataKey="name"
                    stroke="#ff3366"
                    tick={{ fill: "#ffffff" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    stroke="#ff3366"
                    tick={{ fill: "#ffffff" }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    fill={(entry) => entry.color}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legenda de Cores */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(examValues).map(([hormone, value]) => {
              const color = hormoneColors[hormone.toLowerCase()] || "#ff3366";
              return (
                <div
                  key={hormone}
                  className="p-3 rounded-lg border"
                  style={{
                    backgroundColor: `${color}15`,
                    borderColor: `${color}30`
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span className="font-medium text-white">{hormone.toUpperCase()}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-white/90">{value}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Análise Metabólica */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Análise Metabólica
            </h3>
            <MetabolicAnalysis examValues={examValues} />
          </div>

          {/* Consulta IA */}
          <AIConsultation 
            examValues={examValues} 
            results={analysisResults}
          />
        </motion.div>
      )}
    </motion.div>
  );
}
